import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useState,
  RefObject,
} from 'react';
import InputButton from './InputButton';
import styles from './ChatInput.module.css';

export interface ChatInputProps {
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  value?: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onInputButtonClick: () => void;
  inputRef: RefObject<HTMLTextAreaElement>;
}

const ChatInput: FC<ChatInputProps> = ({
  disabled = false,
  value,
  placeholder,
  onKeyDown,
  onChange,
  onFocus,
  onBlur,
  onInputButtonClick,
  inputRef,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [inputScrollHeight, setInputScrollHeight] = useState(40);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // automatically vertically resizes the textarea input for
  // when multiple lines of text are input.
  // FIXME: it goes back to 1 line when the input value is fully
  // cleared, but doesn't decrease height on line deletion before that.
  useEffect(() => {
    if (!inputRef?.current) return;

    if (!inputValue) {
      inputRef.current.style['height'] = '2.5rem';
      setInputScrollHeight(40);
      return;
    }
    const newInputScrollHeight = inputRef?.current?.scrollHeight;
    if (
      typeof newInputScrollHeight === 'number' &&
      newInputScrollHeight !== inputScrollHeight
    ) {
      inputRef.current.style['height'] = `${newInputScrollHeight}px`;
      setInputScrollHeight(newInputScrollHeight);
    }
  }, [inputValue, inputRef, inputScrollHeight]);

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor="chatInputBox">
        {!inputValue ? placeholder : ''}
      </label>
      <textarea
        className={styles.input}
        id="chatInputBox"
        ref={inputRef}
        value={inputValue}
        onTouchEnd={() => {
          // input won't focus on mobile with the keyboard up,
          // but forcing it fixes this.
          inputRef.current?.focus();
        }}
        autoComplete="off"
        onKeyDown={(e): void => {
          if (onKeyDown) onKeyDown(e);
        }}
        onChange={(e): void => {
          setInputValue(e.target.value);
          if (onChange) onChange(e);
        }}
        onFocus={(): void => {
          if (onFocus) onFocus();
        }}
        onBlur={(): void => {
          if (onBlur) onBlur();
        }}
        minLength={1}
        maxLength={300}
      />
      <div className={styles.buttonContainer}>
        <InputButton
          disabled={!inputValue || disabled}
          onClick={onInputButtonClick}
        />
      </div>
    </div>
  );
};

export default ChatInput;
